import { Customer, DynamicField, DynamicSections, Facility, PaymentDetailType } from '@cargos/sprintpay-models';
import moment from 'moment';
import { Detail } from '../details';
import { UserPayments } from '../payment/payment';

export class User implements UserProperties, UserPayments {
    constructor(
        public dynamicSections: DynamicSections[] = [
            {
                id: 1,
                label: 'Payment Details',
                orderNumber: 1,
                emailDomain: null,
            },
            {
                id: 2,
                label: 'Customer Reference',
                orderNumber: 2,
                emailDomain: null,
            },
        ],
        public dynamicFields: DynamicField[] = [
            {
                name: 'customerRef',
                label: 'Reference',
                section: 2,
                fieldType: 'input',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage:
                    'Your own personal reference # so you can remember this payment. For example your file #.',
                validations: [
                    {
                        id: 2,
                        name: 'required',
                        value: 'true',
                        message: 'Required field',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
        ]
    ) {}

    static fromJson({ dynamicFields, dynamicSections, config }: UserProperties): User {
        const newDynamicFields = this.changeDynamicFieldName(dynamicFields, config);
        return new User(dynamicSections, newDynamicFields);
    }

    static changeDynamicFieldName(dynamicFields: DynamicField[] | undefined, config: DynamicField[] | undefined) {
        if (!config?.length) {
            return dynamicFields;
        }

        return dynamicFields?.map((field) => {
            const newDynamicFields = config.find((fieldElement) => {
                if (fieldElement.name === field.name) {
                    return fieldElement;
                }
                return field;
            });
            return { ...field, ...newDynamicFields };
        });
    }

    public getPaymentDetails = (payment: PaymentDetailType, facility: Facility, id: string): Detail[] => [
        {
            label: facility.externalLookup
                ? 'File Number'
                : facility.awbFieldLabel === 'AWB'
                  ? 'RELATED BOL / AWB #'
                  : (facility.awbFieldLabel ?? 'N/A'),
            value: payment.hawb ? payment.hawb : 'N/A',
            show: !facility.externalLookup && !id,
        },
        {
            label: 'Payment Type',
            value: payment.paymentType ? payment.paymentType : 'N/A',
            show: true,
        },
        { label: 'Paid To', value: payment.paidTo ? payment.paidTo : 'N/A', show: true },
        {
            label: 'Cargo facility',
            value: facility.name ? facility.name : 'N/A',
            show: true,
        },
        {
            label: 'Amount',
            value: payment.amount ? '$' + payment.amount.toFixed(2) : 'N/A',
            show: true,
        },
    ];

    public getPaymentCustomerReference = (
        payment: PaymentDetailType,
        customerReferenceLabel: string = '',
        customer?: Customer
    ): Detail[] => [
        {
            label: customer?.expeditors ? 'File #' : 'Reference',
            value: payment.customerReference ? payment.customerReference : 'N/A',
            show: true,
        },
        {
            label: 'GEODIS Local Client Name',
            value: payment.externalReferenceNum ? payment.externalReferenceNum : 'N/A',
            show: !!payment.externalReferenceNum,
        },
        {
            label: 'Payment Notification Email',
            value: payment.notificationEmail ? payment.notificationEmail : 'N/A',
            show: true,
        },
        { label: 'Notes', value: payment.notes ? payment.notes : 'N/A', show: true },
    ];

    public getCustomerInformation = (payment: PaymentDetailType): Detail[] => [
        {
            label: 'Customer Name',
            value: payment.customer ? payment.customer.name : 'N/A',
            show: true,
        },
        {
            label: 'Notification email',
            value: payment?.notificationEmail ? payment?.notificationEmail : 'N/A',
            show: true,
        },
        {
            label: 'Created Date',
            value: payment?.createdDate ? moment(payment?.createdDate).format('MMM DD, YYYY') : 'N/A',
            show: true,
        },
    ];

    public getCompanyCustomerReference = (payment: PaymentDetailType): Detail[] => [
        {
            label: 'Payment Amount',
            value: payment.externalData?.type ?? 'N/A',
            show: true,
        },
        {
            label: 'Type',
            value: payment.paymentType ?? 'N/A',
            show: true,
        },
        { label: 'Location', value: payment.location ?? 'N/A', show: true },
    ];
}

export interface UserProperties {
    dynamicFields?: DynamicField[];
    dynamicSections?: DynamicSections[];
    config?: DynamicField[];
}
